'use client';

import { BookingCountryService } from '@repo/common/interfaces';
import { VpIcon } from '@vtmn-play/react';
import useConfig from '@/hooks/useConfig';
import Callout from '@repo/ui/Callout';
import WidgetServiceItem from './WidgetServiceItem';
import { useTranslations } from 'next-intl';
import StepContainer from '../StepContainer/StepContainer';
import { useWidgetStore } from '@/providers/widget.provider';
import { ReactNode, useCallback } from 'react';
import { CategoryTypeEnum } from '@repo/common/enums';
import { useTrackingAnalytics } from '@/providers/tracking.provider';
import { TrackingEvent } from '@/interfaces/tracking.interface';

import './WidgetServiceStep.scss';

interface WidgetServiceStepProps {
  category?: CategoryTypeEnum;
  countryServices?: BookingCountryService[];
  hideServicePrices?: string[];
}

const WidgetServiceStep = ({
  category,
  countryServices,
  hideServicePrices,
}: WidgetServiceStepProps) => {
  const t = useTranslations('Widget.pages.steps.services');
  const { currentStep, goToNextStep } = useConfig();
  const { categoryType, setCountryService } = useWidgetStore((state) => state);
  const { trackEvent } = useTrackingAnalytics();

  const handleServiceOnClick = (countryService: BookingCountryService) => {
    setCountryService(countryService);

    trackEvent(TrackingEvent.SERVICE_SELECTED, {
      page_name: currentStep?.id,
      category_id: categoryType,
      category_name: categoryType,
      service_id: countryService?.productSheet?.sku,
      service_name: countryService?.productSheet?.name,
    });

    goToNextStep(
      new URLSearchParams({
        sku: countryService?.sku,
      }),
    );
  };

  const paragraphChunksNode = useCallback((chunks: ReactNode) => {
    return <p>{chunks}</p>;
  }, []);

  const hasToShowPrice = useCallback(
    (countryService: BookingCountryService) => {
      return !hideServicePrices?.includes(countryService.sku);
    },
    [hideServicePrices],
  );

  return (
    <StepContainer className='widget-service-step'>
      {countryServices?.length === 0 && (
        <div className='flex flex-col items-center justify-center'>
          <VpIcon name='warning-error' />
          <p className='text-center vp-body-s'>{t('empty')}</p>
        </div>
      )}

      {t.has(`${category?.toLowerCase()}.content.information`) &&
        t(`${category?.toLowerCase()}.content.information`).trim().length >
          0 && (
          <Callout icon='information'>
            {t.rich(`${category?.toLowerCase()}.content.information`, {
              paragraph: paragraphChunksNode,
            })}
          </Callout>
        )}

      <ul>
        {countryServices?.map((countryService: BookingCountryService) => (
          <li key={countryService.sku}>
            <WidgetServiceItem
              onClick={() => handleServiceOnClick(countryService)}
              countryService={countryService}
              showPrice={hasToShowPrice(countryService)}
            />
          </li>
        ))}
      </ul>
    </StepContainer>
  );
};

export default WidgetServiceStep;
